import { useEffect, useMemo, useState } from "react";
import { getPerformanceSummary, getPortfolioHealth, getTradeStats, getTrades } from "./StockTradeService";

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const daysBetween = (start, end) => {
  if (!start || !end) return null;
  const diff = Math.round((end.getTime() - start.getTime()) / 86400000);
  return diff < 0 ? 0 : diff;
};

const buildGroupStats = (trades) => {
  const map = new Map();
  trades.forEach((t) => {
    const key = `${t.tradingAccountId || ""}::${t.symbol || ""}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        accountId: t.tradingAccountId || "",
        symbol: t.symbol || "",
        stockName: t.stockName || "",
        buyQty: 0,
        sellQty: 0,
        buyValue: 0,
        sellValue: 0,
        fees: 0,
        taxes: 0,
        firstBuy: null,
        lastSell: null,
      });
    }
    const bucket = map.get(key);
    const qty = toNumber(t.quantity);
    const price = toNumber(t.price);
    const amount = qty * price;
    const tradeDate = toDate(t.tradeDate);
    if (t.tradeType === "SELL") {
      bucket.sellQty += qty;
      bucket.sellValue += amount;
      if (tradeDate && (!bucket.lastSell || tradeDate > bucket.lastSell)) {
        bucket.lastSell = tradeDate;
      }
    } else {
      bucket.buyQty += qty;
      bucket.buyValue += amount;
      if (tradeDate && (!bucket.firstBuy || tradeDate < bucket.firstBuy)) {
        bucket.firstBuy = tradeDate;
      }
    }
    bucket.fees += toNumber(t.fees);
    bucket.taxes += toNumber(t.taxes);
  });

  const groups = [];
  map.forEach((bucket) => {
    const avgBuy = bucket.buyQty === 0 ? 0 : bucket.buyValue / bucket.buyQty;
    const realizedPnl = bucket.sellValue - avgBuy * bucket.sellQty - bucket.fees - bucket.taxes;
    const holdDays = daysBetween(bucket.firstBuy, bucket.lastSell);
    groups.push({ ...bucket, avgBuy, realizedPnl, holdDays });
  });
  return groups;
};

export default function StockAnalytics({ onSelectSymbol }) {
  const [health, setHealth] = useState({
    totalInvested: 0,
    totalRealizedPnl: 0,
    totalUnrealizedPnl: 0,
    totalPnl: 0,
  });
  const [performance, setPerformance] = useState({ rows: [] });
  const [trades, setTrades] = useState([]);
  const [tradeStats, setTradeStats] = useState({
    totalClosedPositions: 0,
    winningPositions: 0,
    winRatePercent: 0,
    avgRMultiple: 0,
    rMultipleSampleSize: 0,
    strategyBreakdown: [],
  });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const [healthData, perfData, tradeData, statsData] = await Promise.all([
      getPortfolioHealth(),
      getPerformanceSummary(),
      getTrades(),
      getTradeStats(),
    ]);
    setHealth(
      healthData || {
        totalInvested: 0,
        totalRealizedPnl: 0,
        totalUnrealizedPnl: 0,
        totalPnl: 0,
      }
    );
    setPerformance(perfData || { rows: [] });
    setTrades(Array.isArray(tradeData) ? tradeData : []);
    setTradeStats(
      statsData || {
        totalClosedPositions: 0,
        winningPositions: 0,
        winRatePercent: 0,
        avgRMultiple: 0,
        rMultipleSampleSize: 0,
        strategyBreakdown: [],
      }
    );
  };

  const analytics = useMemo(() => {
    const totalTrades = trades.length;
    const notesCount = trades.filter((t) => (t.notes || "").trim().length > 0).length;
    const groups = buildGroupStats(trades);
    const closed = groups.filter((g) => g.sellQty > 0);
    const wins = closed.filter((g) => g.realizedPnl > 0).length;
    const losses = closed.filter((g) => g.realizedPnl < 0).length;
    const winRate = closed.length ? (wins / closed.length) * 100 : 0;
    const grossProfit = closed.reduce((acc, g) => (g.realizedPnl > 0 ? acc + g.realizedPnl : acc), 0);
    const grossLoss = closed.reduce((acc, g) => (g.realizedPnl < 0 ? acc + Math.abs(g.realizedPnl) : acc), 0);
    const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : null;
    const holdingDays = closed.map((g) => g.holdDays).filter((d) => d !== null);
    const avgHoldDays = holdingDays.length
      ? holdingDays.reduce((acc, d) => acc + d, 0) / holdingDays.length
      : 0;
    const bestTrade = closed.reduce((best, g) => (best && best.realizedPnl > g.realizedPnl ? best : g), null);
    const worstTrade = closed.reduce((worst, g) => (worst && worst.realizedPnl < g.realizedPnl ? worst : g), null);

    return {
      totalTrades,
      notesCount,
      closedCount: closed.length,
      winRate,
      avgHoldDays,
      profitFactor,
      bestTrade,
      worstTrade,
      wins,
      losses,
    };
  }, [trades]);

  const formatPercent = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return "--";
    return `${Number(value).toFixed(2)}%`;
  };

  const formatPrice = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return "--";
    return Number(value).toFixed(2);
  };

  const changeClass = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return "";
    return Number(value) >= 0 ? "stocks-good" : "stocks-bad";
  };

  const perfRows = useMemo(() => performance?.rows || [], [performance]);

  return (
    <div className="stocks-section">
      <div className="stocks-analytics-grid">
        <div className="stocks-stat-card">
          <div className="stocks-stat-label">Total Invested</div>
          <div className="stocks-stat-value">{health.totalInvested || 0}</div>
          <div className="stocks-stat-sub">Unrealized: {health.totalUnrealizedPnl || 0}</div>
        </div>
        <div className="stocks-stat-card">
          <div className="stocks-stat-label">Total PnL</div>
          <div className={`stocks-stat-value ${health.totalPnl >= 0 ? "stocks-good" : "stocks-bad"}`}>
            {health.totalPnl || 0}
          </div>
          <div className="stocks-stat-sub">Realized: {health.totalRealizedPnl || 0}</div>
        </div>
        <div className="stocks-stat-card">
          <div className="stocks-stat-label">Trades Logged</div>
          <div className="stocks-stat-value">{analytics.totalTrades}</div>
          <div className="stocks-stat-sub">Journaled: {analytics.notesCount}</div>
        </div>
        <div className="stocks-stat-card">
          <div className="stocks-stat-label">Win Rate</div>
          <div className="stocks-stat-value">{formatPercent(analytics.winRate)}</div>
          <div className="stocks-stat-sub">
            Wins {analytics.wins} · Losses {analytics.losses}
          </div>
        </div>
        <div className="stocks-stat-card">
          <div className="stocks-stat-label">Avg Hold (days)</div>
          <div className="stocks-stat-value">{analytics.avgHoldDays.toFixed(1)}</div>
          <div className="stocks-stat-sub">Closed positions: {analytics.closedCount}</div>
        </div>
        <div className="stocks-stat-card">
          <div className="stocks-stat-label">Profit Factor</div>
          <div className="stocks-stat-value">
            {analytics.profitFactor === null ? "--" : analytics.profitFactor.toFixed(2)}
          </div>
          <div className="stocks-stat-sub">Gross wins vs losses</div>
        </div>
        <div className="stocks-stat-card">
          <div className="stocks-stat-label">Best Trade</div>
          <div className={`stocks-stat-value ${analytics.bestTrade?.realizedPnl >= 0 ? "stocks-good" : "stocks-bad"}`}>
            {analytics.bestTrade ? analytics.bestTrade.realizedPnl.toFixed(2) : "--"}
          </div>
          <div className="stocks-stat-sub">{analytics.bestTrade?.symbol || "No closed trades"}</div>
        </div>
        <div className="stocks-stat-card">
          <div className="stocks-stat-label">Worst Trade</div>
          <div className={`stocks-stat-value ${analytics.worstTrade?.realizedPnl >= 0 ? "stocks-good" : "stocks-bad"}`}>
            {analytics.worstTrade ? analytics.worstTrade.realizedPnl.toFixed(2) : "--"}
          </div>
          <div className="stocks-stat-sub">{analytics.worstTrade?.symbol || "No closed trades"}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">Trade Performance</div>
        <div className="stocks-analytics-grid">
          <div className="stocks-stat-card">
            <div className="stocks-stat-label">Win Rate (closed)</div>
            <div className="stocks-stat-value">{formatPercent(tradeStats.winRatePercent)}</div>
            <div className="stocks-stat-sub">
              {tradeStats.winningPositions} / {tradeStats.totalClosedPositions} positions
            </div>
          </div>
          <div className="stocks-stat-card">
            <div className="stocks-stat-label">Avg R-Multiple</div>
            <div className={`stocks-stat-value ${changeClass(tradeStats.avgRMultiple)}`}>
              {tradeStats.rMultipleSampleSize > 0 ? `${toNumber(tradeStats.avgRMultiple).toFixed(2)}R` : "--"}
            </div>
            <div className="stocks-stat-sub">
              {tradeStats.rMultipleSampleSize > 0
                ? `Sample size: ${tradeStats.rMultipleSampleSize}`
                : "Set stop-loss on trades to see R-multiples"}
            </div>
          </div>
        </div>
        {tradeStats.strategyBreakdown.length > 0 && (
          <div className="data-table-wrap" style={{ marginTop: 12 }}>
            <table className="stocks-table">
              <thead>
                <tr>
                  <th>Strategy</th>
                  <th>Closed</th>
                  <th>Win Rate</th>
                  <th>Avg R</th>
                  <th>R Sample</th>
                </tr>
              </thead>
              <tbody>
                {tradeStats.strategyBreakdown.map((s) => (
                  <tr key={s.strategyTag}>
                    <td>{s.strategyTag}</td>
                    <td>{s.closedPositions}</td>
                    <td>{formatPercent(s.winRatePercent)}</td>
                    <td className={changeClass(s.rMultipleSampleSize > 0 ? s.avgRMultiple : null)}>
                      {s.rMultipleSampleSize > 0 ? `${toNumber(s.avgRMultiple).toFixed(2)}R` : "--"}
                    </td>
                    <td>{s.rMultipleSampleSize}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-title">Performance Snapshots</div>
        <div className="data-table-wrap">
          <table className="stocks-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Last Price</th>
                <th>15D</th>
                <th>1M</th>
                <th>2M</th>
                <th>Q</th>
                <th>6M</th>
                <th>1Y</th>
              </tr>
            </thead>
            <tbody>
              {perfRows.map((row) => (
                <tr key={row.stockId}>
                  <td>
                    {onSelectSymbol ? (
                      <button className="btn btn-ghost btn-sm" onClick={() => onSelectSymbol(row.symbol, row.stockName)}>
                        {row.symbol}
                      </button>
                    ) : (
                      row.symbol
                    )}
                  </td>
                  <td>{formatPrice(row.latestPrice)}</td>
                  <td className={changeClass(row.change15d)}>{formatPercent(row.change15d)}</td>
                  <td className={changeClass(row.change1m)}>{formatPercent(row.change1m)}</td>
                  <td className={changeClass(row.change2m)}>{formatPercent(row.change2m)}</td>
                  <td className={changeClass(row.changeQuarter)}>{formatPercent(row.changeQuarter)}</td>
                  <td className={changeClass(row.changeHalfYear)}>{formatPercent(row.changeHalfYear)}</td>
                  <td className={changeClass(row.changeYear)}>{formatPercent(row.changeYear)}</td>
                </tr>
              ))}
              {perfRows.length === 0 && (
                <tr>
                  <td colSpan="8">No performance data yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
